import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  getDatabase,
  ref as dbRef,
  get,
  set,
  remove,
  serverTimestamp,
} from "firebase/database";

import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Stack,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormGroup,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ShieldIcon from "@mui/icons-material/Shield";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import "./scheduler.scss";

const CATEGORIES = [
  "accelerometer",
  "bodyComposition",
  "ecg",
  "heartRate",
  "ppg",
  "skinTemperature",
  "spo2",
  "electrodermalActivity",
];

const TIMEFRAME_OPTIONS = [
  { value: "1week", label: "1 Week", ms: 7 * 24 * 60 * 60 * 1000 },
  { value: "1month", label: "1 Month", ms: 30 * 24 * 60 * 60 * 1000 },
  { value: "3months", label: "3 Months", ms: 3 * 30 * 24 * 60 * 60 * 1000 },
  { value: "6months", label: "6 Months", ms: 6 * 30 * 24 * 60 * 60 * 1000 },
];

const AutoDeleteComponent = ({ watchId, onClose }) => {
  const [enabled, setEnabled] = useState(false);
  const [timeframe, setTimeframe] = useState("1month");
  const [busy, setBusy] = useState(false);
  const [lastRunInfo, setLastRunInfo] = useState("");

  // Confirmation dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAck, setConfirmAck] = useState(false);

  const nodePath = (watchId) => `Health/${watchId}/autoDeleteRecords`;
  const db = useMemo(() => getDatabase(), []);

  const timeframeMs = useMemo(() => {
    return (
      TIMEFRAME_OPTIONS.find((t) => t.value === timeframe)?.ms ??
      TIMEFRAME_OPTIONS[1].ms
    ); // default 1 month
  }, [timeframe]);

  // Load settings
  const loadSettings = useCallback(async () => {
    if (!watchId) return;
    try {
      const snap = await get(dbRef(db, nodePath(watchId)));
      if (snap.exists()) {
        const val = snap.val();
        if (typeof val?.enabled === "boolean") setEnabled(val.enabled);
        if (typeof val?.timeframe === "string") setTimeframe(val.timeframe);
      }
    } catch (e) {
      console.error("Failed to load auto-delete settings:", e);
    }
  }, [db, watchId]);

  // Save settings
  const saveSettings = useCallback(async () => {
    if (!watchId) return;
    try {
      await set(dbRef(db, nodePath(watchId)), {
        enabled,
        timeframe,
        updatedAt: serverTimestamp(),
      });
      alert("Auto-delete settings saved.");
    } catch (e) {
      console.error("Failed to save settings:", e);
      alert("Failed to save settings.");
    }
  }, [db, watchId, enabled, timeframe]);

  // Deletion runner
  const runAutoDelete = useCallback(async () => {
    if (!watchId || !enabled) return;
    setBusy(true);
    try {
      const now = Date.now();
      const cutoff = now - timeframeMs;
      let totalDeleted = 0;
      let totalChecked = 0;

      for (const cat of CATEGORIES) {
        const catRef = dbRef(db, `Health/${watchId}/${cat}`);
        const snap = await get(catRef);
        if (!snap.exists()) continue;
        const data = snap.val();

        const ids = Object.keys(data || {});
        for (const id of ids) {
          const ts = Number(id);
          if (Number.isFinite(ts)) {
            totalChecked++;
            if (ts < cutoff) {
              await remove(dbRef(db, `Health/${watchId}/${cat}/${id}`));
              totalDeleted++;
            }
          }
        }
      }

      setLastRunInfo(
        `Auto-delete checked ${totalChecked} records and removed ${totalDeleted} older than ${
          TIMEFRAME_OPTIONS.find((t) => t.value === timeframe)?.label
        }.`
      );
      if (totalDeleted > 0) {
        alert(`${totalDeleted} record${totalDeleted > 1 ? "s" : ""} deleted.`);
      }
    } catch (e) {
      console.error("Auto-delete failed:", e);
      alert("Auto-delete failed. Check console for details.");
    } finally {
      setBusy(false);
    }
  }, [db, watchId, enabled, timeframe, timeframeMs]);

  // Effects
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Run automatically whenever enabled/timeframe changes
  useEffect(() => {
    if (enabled) {
      runAutoDelete();
    }
  }, [enabled, timeframe, runAutoDelete]);

  const handleToggle = (_event, checked) => {
    if (checked && !enabled) {
      setConfirmOpen(true);
      setConfirmAck(false);
      return;
    }
    // Turning ON->OFF or redundant action
    setEnabled(false);
  };

  const handleConfirmEnable = () => {
    if (!confirmAck) return;
    setEnabled(true);
    setConfirmOpen(false);
  };

  const handleCancelEnable = () => {
    setConfirmOpen(false);
    setConfirmAck(false);
    // keep as disabled
    setEnabled(false);
  };

  return (
    <div className="autodelete-shell">
      <div className="scheduler-header">
        <div className="scheduler-title">
          <div className="icon-chip">
            <DeleteSweepIcon />
          </div>
          <div>
            <p className="eyebrow">Data hygiene</p>
            <h3>Auto-delete old records</h3>
            <div className="header-meta">
              <Chip
                size="small"
                icon={<ShieldIcon />}
                label={enabled ? "Protection on" : "Protection off"}
                className={enabled ? "ghost-chip" : "soft-chip"}
                color={enabled ? "success" : "default"}
              />
              <Chip
                size="small"
                icon={<HistoryToggleOffIcon />}
                label={`Timeframe • ${
                  TIMEFRAME_OPTIONS.find((t) => t.value === timeframe)?.label
                }`}
                className="soft-chip"
                variant="outlined"
              />
            </div>
          </div>
        </div>

        <div className="header-actions">
          <Button
            variant="outlined"
            startIcon={<BoltIcon />}
            onClick={runAutoDelete}
            disabled={!enabled || busy}
          >
            Run now
          </Button>
          {onClose && (
            <IconButton onClick={onClose} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          )}
        </div>
      </div>

      <div className="scheduler-grid">
        <div className="scheduler-card">
          <div className="card-head">
            <div className="icon-chip hollow">
              <CheckCircleIcon />
            </div>
            <div>
              <h4>Controls</h4>
              <p className="muted">Enable cleanup and choose how far back to keep data.</p>
            </div>
          </div>

          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={handleToggle}
                  color="primary"
                />
              }
              label="Enable Auto-Delete"
            />

            <FormControl fullWidth>
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={timeframe}
                label="Timeframe"
                onChange={(e) => setTimeframe(e.target.value)}
                disabled={!enabled}
              >
                {TIMEFRAME_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button variant="contained" onClick={saveSettings} disabled={busy} fullWidth>
                Save settings
              </Button>
              <Button
                variant="outlined"
                onClick={runAutoDelete}
                disabled={!enabled || busy}
                fullWidth
              >
                Run now
              </Button>
            </Stack>

            {busy && <LinearProgress />}

            {lastRunInfo && (
              <Typography variant="body2" color="text.secondary">
                {lastRunInfo}
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary">
              Applies to: {CATEGORIES.join(", ")}
            </Typography>
          </Stack>
        </div>

        <div className="scheduler-card list-card">
          <div className="card-head">
            <div className="icon-chip hollow">
              <ShieldIcon />
            </div>
            <div>
              <h4>Safety details</h4>
              <p className="muted">What happens when auto-delete is on.</p>
            </div>
          </div>

          <List dense className="schedule-list">
            <ListItem>
              <ListItemText
                primary="Older records are permanently removed"
                secondary="Items beyond the selected timeframe are deleted without recovery."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Runs immediately and after changes"
                secondary="Cleanup executes now and whenever the timeframe or toggle updates."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Covers all categories"
                secondary={`Affects: ${CATEGORIES.join(", ")}`}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Reads and writes are required"
                secondary="Scans your records to find anything past the cutoff and issues delete calls."
              />
            </ListItem>
          </List>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelEnable}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enable Auto-Delete?</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Turning this on will automatically and permanently remove older
            data.
          </Alert>

          <Typography variant="subtitle2" gutterBottom>
            What happens if you choose <strong>Yes</strong>:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Older records will be permanently deleted"
                secondary="Items older than the selected timeframe are removed with no built-in undo."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Deletion runs now and on future changes"
                secondary="A cleanup runs immediately after enabling and whenever the timeframe changes."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Applies across categories"
                secondary={`Affects: ${CATEGORIES.join(", ")}`}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="May increase read/write activity"
                secondary="Reads existing records to check age and issues deletes for items past the cutoff."
              />
            </ListItem>
          </List>

          <FormGroup sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmAck}
                  onChange={(e) => setConfirmAck(e.target.checked)}
                />
              }
              label="I understand the consequences and want to enable Auto-Delete."
            />
            {!confirmAck && (
              <FormHelperText>
                You must acknowledge before proceeding.
              </FormHelperText>
            )}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEnable}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmEnable}
            disabled={!confirmAck}
            autoFocus
          >
            Yes, enable
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AutoDeleteComponent;
